import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserPlus, Trash2, Loader2, Crown, ShieldCheck, Clock } from "lucide-react";

export default function AdminMembers({ open, onClose }) {
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/members");
      setMembers(res.data.members);
      setInvites(res.data.pending_invites);
    } catch {
      toast.error("Failed to load team");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleInvite = async () => {
    if (!email.trim()) return;
    setInviting(true);
    try {
      const res = await api.post("/admin/invite", { email: email.trim() });
      toast.success(res.data.promoted ? "User promoted to Admin" : "Invite sent");
      setEmail("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to invite");
    } finally {
      setInviting(false);
    }
  };

  const revokeMember = async (m) => {
    try {
      await api.delete(`/admin/members/${m.user_id}`);
      toast.success(`${m.email} removed as admin`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to revoke");
    }
  };

  const revokeInvite = async (inviteEmail) => {
    try {
      await api.delete(`/admin/invite/${encodeURIComponent(inviteEmail)}`);
      toast.success("Invite revoked");
      load();
    } catch {
      toast.error("Failed to revoke invite");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-surface border border-white/15 rounded-none max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="members-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading font-700 text-2xl tracking-tight text-white uppercase">
            Manage Team
          </DialogTitle>
          <DialogDescription className="font-mono text-xs text-white/40">
            Invite admins by email. Only the Super Admin can add or remove admins.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-8">
          {/* Invite form */}
          <div>
            <label className="font-mono text-xs uppercase tracking-wider text-glow-primary">Invite Admin</label>
            <div className="flex gap-2 mt-2">
              <Input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleInvite()}
                placeholder="name@example.com"
                type="email"
                className="bg-transparent border-white/20 text-white rounded-none focus:border-glow-primary focus-visible:ring-1 focus-visible:ring-glow-primary font-mono"
                data-testid="invite-email-input"
              />
              <Button
                onClick={handleInvite}
                disabled={inviting}
                className="bg-glow-primary text-black font-bold hover:bg-glow-primary/80 rounded-none font-mono text-xs uppercase tracking-wider shrink-0"
                data-testid="invite-submit-btn"
              >
                {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-2" /> Invite</>}
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="py-8 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-glow-primary" />
            </div>
          ) : (
            <>
              {/* Members */}
              <div>
                <p className="font-mono text-xs uppercase tracking-wider text-white/40 mb-3">
                  Members ({members.length})
                </p>
                <div className="space-y-2" data-testid="members-list">
                  {members.map((m) => (
                    <div
                      key={m.user_id}
                      className="flex items-center justify-between border border-white/10 p-3"
                      data-testid={`member-${m.user_id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {m.picture ? (
                          <img src={m.picture} alt="" className="w-8 h-8 rounded-full border border-white/20" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-white/10" />
                        )}
                        <div className="min-w-0">
                          <p className="font-mono text-sm text-white truncate">{m.email}</p>
                          <span className={`font-mono text-[10px] uppercase tracking-wider flex items-center gap-1 ${m.role === "super_admin" ? "text-glow-accent" : "text-glow-primary"}`}>
                            {m.role === "super_admin" ? <Crown className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />}
                            {m.role === "super_admin" ? "Super Admin" : "Admin"}
                          </span>
                        </div>
                      </div>
                      {m.role !== "super_admin" && (
                        <Button
                          onClick={() => revokeMember(m)}
                          variant="outline"
                          size="sm"
                          className="border-glow-secondary/50 text-glow-secondary hover:bg-glow-secondary/10 rounded-none shrink-0"
                          aria-label="Revoke admin"
                          data-testid={`revoke-member-${m.user_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Pending invites */}
              {invites.length > 0 && (
                <div>
                  <p className="font-mono text-xs uppercase tracking-wider text-white/40 mb-3">
                    Pending Invites ({invites.length})
                  </p>
                  <div className="space-y-2" data-testid="invites-list">
                    {invites.map((inv) => (
                      <div
                        key={inv.email}
                        className="flex items-center justify-between border border-dashed border-white/15 p-3"
                        data-testid={`invite-${inv.email}`}
                      >
                        <span className="font-mono text-sm text-white/70 flex items-center gap-2 min-w-0 truncate">
                          <Clock className="w-3.5 h-3.5 text-glow-accent shrink-0" /> {inv.email}
                        </span>
                        <Button
                          onClick={() => revokeInvite(inv.email)}
                          variant="outline"
                          size="sm"
                          className="border-white/20 text-white/60 hover:bg-white/5 rounded-none shrink-0"
                          aria-label="Revoke invite"
                          data-testid={`revoke-invite-${inv.email}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
